/**
 * /table · DidYouMean — honest spelling suggestion line for low-result
 * searches.
 *
 * Pure frontend · uses Levenshtein distance over our city + cuisine
 * lexicons (the same lists the typeahead + live-intent badge use, so
 * no source of truth drift). Renders only when:
 *   - the search returned <= 2 results, AND
 *   - at least one token in the member's query is within edit
 *     distance 2 of a known city/cuisine but is not an exact match.
 *
 * Doctrine: we suggest, never auto-correct. The member sees a chip
 * "Did you mean Bengaluru?" and decides.
 */
import React, { useMemo } from "react";

const CITIES = [
    "Bangkok", "Bangalore", "Bengaluru", "Mumbai", "Delhi", "New Delhi",
    "Kolkata", "Chennai", "Hyderabad", "Pune", "Goa", "Jaipur", "Udaipur",
    "Paris", "London", "Tokyo", "Kyoto", "Osaka", "Lima", "Lisbon",
    "Barcelona", "Madrid", "Berlin", "Vienna", "Rome", "Milan",
    "Dubai", "Abu Dhabi", "Doha", "Istanbul", "Singapore", "Hong Kong",
    "Shanghai", "Seoul", "Sydney", "Melbourne", "New York", "Manhattan",
    "San Francisco", "Los Angeles", "Chicago", "Miami", "Boston",
];

const CUISINES = [
    "Italian", "Chinese", "Japanese", "Korean", "Thai", "Vietnamese",
    "Indian", "French", "Spanish", "Mexican", "American", "British",
    "Greek", "Turkish", "Lebanese", "Moroccan", "Mediterranean",
    "Peruvian", "Sushi", "Ramen", "Omakase", "Pizza", "Pasta",
    "Tapas", "BBQ", "Steakhouse", "Seafood", "Vegetarian", "Vegan",
];

const ALL = [...CITIES, ...CUISINES];

/** Cheap Levenshtein for tokens up to ~12 chars (good enough). */
function distance(a, b) {
    a = a.toLowerCase();
    b = b.toLowerCase();
    if (a === b) return 0;
    if (a.length < 2 || b.length < 2) return 99;
    const m = a.length;
    const n = b.length;
    if (Math.abs(m - n) > 3) return 99;
    const dp = Array(n + 1).fill(0).map((_, j) => j);
    for (let i = 1; i <= m; i++) {
        let prev = dp[0];
        dp[0] = i;
        for (let j = 1; j <= n; j++) {
            const cur = dp[j];
            dp[j] = a[i - 1] === b[j - 1]
                ? prev
                : 1 + Math.min(prev, dp[j], dp[j - 1]);
            prev = cur;
        }
    }
    return dp[n];
}

function suggestionsForQuery(query) {
    const tokens = (query || "")
        .replace(/[,.()'"]/g, " ")
        .split(/\s+/)
        .filter((t) => t.length >= 3);
    const lowAll = ALL.map((x) => x.toLowerCase());
    const found = new Set();
    for (const t of tokens) {
        const tl = t.toLowerCase();
        // exact match? skip
        if (lowAll.includes(tl)) continue;
        // find nearest within edit distance 2
        let best = null;
        for (const cand of ALL) {
            const d = distance(tl, cand);
            if (d <= 2 && (!best || d < best.d)) {
                best = { cand, d };
            }
        }
        if (best) found.add(best.cand);
    }
    return [...found].slice(0, 3);
}


export function DidYouMean({ query, resultCount, onPick }) {
    const suggestions = useMemo(
        () => (resultCount > 2 ? [] : suggestionsForQuery(query)),
        [query, resultCount],
    );

    if (suggestions.length === 0) return null;

    return (
        <div
            data-testid="did-you-mean"
            className="t-fade flex flex-wrap items-center gap-2"
            style={{
                background: "var(--t-paper2)",
                border: "1px solid var(--t-line)",
                borderRadius: 12,
                padding: "10px 14px",
                marginBottom: 14,
                fontSize: 13,
                color: "var(--t-ink2)",
            }}
        >
            <span
                className="t-mono uppercase"
                style={{
                    fontSize: 10,
                    letterSpacing: "0.18em",
                    color: "var(--t-ink3)",
                }}
            >
                did you mean
            </span>
            {suggestions.map((s, i) => (
                <button
                    key={s}
                    type="button"
                    data-testid={`did-you-mean-${i}`}
                    onClick={() => onPick?.(s)}
                    style={{
                        background: "transparent",
                        border: "1px solid var(--t-brass2)",
                        borderRadius: 999,
                        padding: "4px 12px",
                        color: "var(--t-brass)",
                        fontSize: 12.5,
                        cursor: "pointer",
                        fontFamily: "'Hanken Grotesk', system-ui, sans-serif",
                        transition: "all 120ms ease",
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.background = "var(--t-brass)";
                        e.currentTarget.style.color = "var(--t-paper)";
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.background = "transparent";
                        e.currentTarget.style.color = "var(--t-brass)";
                    }}
                >
                    {s}
                </button>
            ))}
            <span style={{ color: "var(--t-ink3)", fontSize: 12 }}>
                — click to refine the search
            </span>
        </div>
    );
}

export { suggestionsForQuery };
